<template>
  <div>
    <!-- メンタリングスケジュール -->
    <ClientOnly>
      <table class="table mt-6 table-fixed whitespace-nowrap border-b border-b-[#dee2e6] text-center">
        <thead
          class="sticky top-[4.375rem]"
          :class="{ 'bg-[#62a5b6]': range !== 2, 'bg-[#5cb85c]': range === 2 }"
        >
          <tr>
            <th class="w-12 border-0 px-3 md:w-[3.75rem]" />
            <th
              v-for="day in appointmentDays"
              :key="day"
              class="border-0 p-2 font-bold text-white"
            >
              <span
                class="block leading-tight"
              >
                {{ day ? $formatDate(day, 'mm/dd') : "-" }}
                <br>
                ({{ day ? $formatDayOfWeek(day) : "-" }})
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(time, rowIndex) in appointmentTimes"
            :key="rowIndex"
            class="border-b-[#dee2e6]"
          >
            <td class="px-3 align-middle">
              {{ time }}~
            </td>
            <template
              v-for="(tab, columnIndex) in appointmentTable[rowIndex]"
              :key="columnIndex"
            >
              <td
                v-if="tab"
                class="p-1 align-middle"
              >
                <!-- モーダルを開くボタン -->
                <button
                  class="flex w-full items-center justify-center rounded bg-white p-2.5 font-bold shadow-[0_0_4px_rgba(51,51,51,0.2)] hover:bg-[#eaf6ea] md:p-3"
                  :value="dateToDateTimeStr(getAppointmentDate(time, columnIndex))"
                  @click="sendAppointmentDate(time, columnIndex)"
                >
                  <FontAwesomeIcon
                    :icon="faCircle"
                    class="fa-fw size-4 text-co-success-500 md:size-6"
                  />
                </button>
              </td>
              <td
                v-if="!tab"
                class="co-display-13 p-1 align-middle leading-tight text-co-gray-200"
              >
                空席
                <br class="inline md:hidden">
                なし
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </ClientOnly>

    <!-- モーダル -->
    <TeCounselingApplyMatchingModal
      ref="calendarModal"
      :appointment-form="appointmentForm"
      :appointment-date="appointmentDate"
      :range="range"
      :purpose="purpose"
      :motivation="motivation"
      :age="age"
      :gender="gender"
      :page-url="pageUrl"
    />
  </div>
</template>

<script lang="ts">
import { daysOfWeek } from '~/utility/utility'
import TeCounselingApplyMatchingModal from '~/components/Te/TeCounseling/Apply/TeCounselingApplyMatchingModal.vue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCircle } from '@awesome.me/kit-65d95819ce/icons/classic/regular'

interface Data {
  appointmentDate: Date | null
}

interface Methods {
  create1WeeksArray: (date: Date | null) => Date[] | null[]
  transpose: (arr: boolean[][]) => boolean[][]
  sendAppointmentDate: (time: string, index: number) => void
  getAppointmentDate: (time: string, index: number) => void
  dateToStr: (date: Date) => string
  dateToDateTimeStr: (date: Date) => string
}

interface Computed {
  table: boolean[][] | null
  appointmentDays: Date[] | null[]
  appointmentTimes: string[]
  appointmentTable: boolean[][]
}

interface Props {
  appointmentForm: any
  appointmentFormTable: any
  range: number
  purpose: string
  motivation: number
  age: number
  gender: number
  pageUrl: string
}

const options = {
  async setup() {

    // モーダルの開閉制御
    const calendarModal = ref(null)

    const showModal = () => {
      if (calendarModal.value) {
        calendarModal.value.showModal()
      }
    }

    return {
      faCircle,
      calendarModal,
      showModal,
    }
  },
  props: {
    appointmentForm: {
      default: null,
    },
    appointmentFormTable: {
      default: null,
    },
    range: {
      default: 1,
    },
    purpose: {
      default: '',
    },
    motivation: {
      default: 0,
    },
    age: {
      default: 0,
    },
    gender: {
      default: 0,
    },
    pageUrl: {
      default: '',
    },
  },
  data() {
    return {
      appointmentDate: null,
    }
  },
  mounted() {},
  computed: {
    table() {
      if (!this.appointmentFormTable) return null
      return this.appointmentFormTable.table
    },
    appointmentDays() {
      if (!this.appointmentForm) return this.create1WeeksArray(null)
      const submittedBeginsAtDate = new Date(
        this.appointmentForm.submitted_begins_at
      )
      return this.create1WeeksArray(submittedBeginsAtDate)
    },
    appointmentTimes() {
      const rangeFrom0To31 = Array.from(Array(32), (_, k) => k)
      const times = rangeFrom0To31.map((num, i) => {
        if (i % 2 === 0) {
          return `${8 + num / 2}:00`
        } else {
          return `${Math.floor(8 + (num - 1) / 2)}:30`
        }
      })
      return times
    },
    appointmentTable() {
      if (!this.table) {
        const initialValue = [...Array(32)].map(() => {
          return [...Array(7)].map(() => {
            return false
          })
        })
        return initialValue
      } else {
        return this.transpose(this.table)
      }
    },
  },
  methods: {
    create1WeeksArray(date: Date | null): Date[] | null[] {
      if (!date) return [...Array(7)].map(() => null)
      const today = new Date(date) as Date
      const days = [...Array(7)].map((_, index) => {
        const day = new Date(date)
        day.setDate(today.getDate() + index)
        day.setHours(0)
        day.setMinutes(0)
        day.setSeconds(0)
        return day
      })
      return days
    },
    transpose(a) {
      return a[0].map((_, c) => a.map((r) => r[c]))
    },
    sendAppointmentDate(time, index) {
      const date = this.getAppointmentDate(time, index)
      if (date == null) return null
      this.appointmentDate = date
      // モーダルを開く
      this.showModal()
    },
    getAppointmentDate(time, index) {
      const day = this.appointmentDays[index]
      if (!day) return null
      const [hour, minute] = time.split(':')
      const date = new Date(
        day.getFullYear(),
        day.getMonth(),
        day.getDate(),
        Number(hour),
        Number(minute)
      )
      return date
    },
    dateToStr(date) {
      if (date == null) return '-/- (-)'
      return `${date.getMonth() + 1}/${date.getDate()}<br>(${daysOfWeek(date)})`
    },
    dateToDateTimeStr(date) {
      if (date == null) return '-/-/- --:--:--'
      return `${date.getFullYear()}/${(date.getMonth() + 1)
        .toString()
        .padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')} ${date
        .getHours()
        .toString()
        .padStart(2, '0')}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
    },
  },
  components: {
    TeCounselingApplyMatchingModal,
    FontAwesomeIcon,
  },
}

export default defineNuxtComponent(options)
</script>
